/**
 * The admin portal's ONE way to print a date, and a date with its time.
 *
 * Before this, three screens on the same store page printed three formats —
 * the tier card `toLocaleString` with seconds ("8/10/2026، 8:14:07 ص"), the
 * history "Oct 8, 2026, 8:14 AM", the payments "10/8/2026" — and a numeric
 * day/month order is ambiguous between the two locales a reader may carry in
 * their head. A month NAME is not: "8 Oct 2026, 8:14 AM" / "8 أكتوبر 2026، 8:14 ص".
 *
 * Digits are pinned Latin (`-u-nu-latn`). That is the portal's convention —
 * every money figure and table date in admin already renders Latin digits,
 * because they format with the bare `ar` tag — and pinning it here keeps a
 * date from switching to Arabic-Indic digits beside a Latin price on a
 * browser whose ICU defaults differ.
 *
 * Date and time are formatted separately and joined with the locale's comma:
 * `Intl`'s combined form inserts "at" / "في", and puts the English day after
 * the month.
 */
const AR = "ar-u-nu-latn";

function parse(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === "") return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function dateFormatter(locale: string) {
  return locale === "ar"
    ? new Intl.DateTimeFormat(AR, { day: "numeric", month: "long", year: "numeric" })
    : new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function timeFormatter(locale: string) {
  return new Intl.DateTimeFormat(locale === "ar" ? AR : "en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

/** "8 Oct 2026" / "8 أكتوبر 2026"; `fallback` for a missing or invalid value. */
export function formatDate(
  value: string | number | Date | null | undefined,
  locale: string,
  fallback = "—",
): string {
  const date = parse(value);
  return date ? dateFormatter(locale).format(date) : fallback;
}

/** "8 Oct 2026, 8:14 AM" / "8 أكتوبر 2026، 8:14 ص" — never seconds. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  locale: string,
  fallback = "—",
): string {
  const date = parse(value);
  if (!date) return fallback;
  const separator = locale === "ar" ? "، " : ", ";
  return `${dateFormatter(locale).format(date)}${separator}${timeFormatter(locale).format(date)}`;
}
